/**
 * SupplierExtractor — generous extracted-fields list UI.
 *
 * Shows each detected primitive as a row with its extracted value.
 * Values are editable inline before commit.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconCheck, IconPencil, IconX } from "@tabler/icons-react"
import clsx from "clsx"
import { useState } from "react"

export interface ExtractedField {
  primitiveId: string
  primitiveTitle: string
  value: string | null
  editedValue?: string | null // user override
}

interface Props {
  filename: string
  supplierName: string | null
  fields: ExtractedField[]
  onFieldEdit: (primitiveId: string, value: string | null) => void
  onVerwerfen: () => void
  onUebernehmen: () => void
  isCommitting: boolean
  error?: string | null
}

export function SupplierExtractor({
  filename,
  supplierName,
  fields,
  onFieldEdit,
  onVerwerfen,
  onUebernehmen,
  isCommitting,
  error,
}: Props) {
  const { translate } = useTranslation()
  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-grey4 px-6 py-4">
        <p className="text-sm text-grey5">
          {translate("QMS/SUPPLIER/SOURCE_DOCUMENT")}
        </p>
        <p className="mt-0.5 font-medium text-white">{filename}</p>
        {supplierName && (
          <>
            <p className="mt-2 text-sm text-grey5">
              {translate("QMS/SUPPLIER/DETECTED_SUPPLIER")}
            </p>
            <p className="mt-0.5 text-sm font-medium text-yellow1">
              {supplierName}
            </p>
          </>
        )}
      </div>

      {/* Fields list */}
      <div className="flex-1 overflow-y-auto">
        <div className="divide-y divide-grey4/40">
          {fields.map((field) => (
            <FieldRow
              key={field.primitiveId}
              field={field}
              onEdit={(v) => onFieldEdit(field.primitiveId, v)}
            />
          ))}
        </div>
        {fields.length === 0 && (
          <p className="px-6 py-8 text-center text-sm text-grey5">
            {translate("QMS/SUPPLIER/NO_FIELDS_DETECTED")}
          </p>
        )}
      </div>

      {error && (
        <div className="mx-6 mb-2 rounded border border-red1/30 bg-red1/10 px-3 py-2 text-xs text-red1">
          {error}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 border-t border-grey4 px-6 py-4">
        <button
          onClick={onVerwerfen}
          disabled={isCommitting}
          className="rounded-md border border-grey4 px-4 py-2 text-sm text-grey5 hover:border-grey5 hover:text-white disabled:opacity-40"
        >
          {translate("QMS/SUPPLIER/DISCARD")}
        </button>
        <button
          onClick={onUebernehmen}
          disabled={isCommitting}
          className="rounded-md bg-yellow1 px-4 py-2 text-sm font-medium text-black hover:bg-yellow5/80 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isCommitting
            ? translate("QMS/SUPPLIER/SAVING")
            : translate("QMS/SUPPLIER/APPLY")}
        </button>
      </div>
    </div>
  )
}

function FieldRow({
  field,
  onEdit,
}: {
  field: ExtractedField
  onEdit: (value: string | null) => void
}) {
  const { translate } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")
  const displayValue =
    field.editedValue !== undefined ? field.editedValue : field.value

  const startEdit = () => {
    setDraft(displayValue ?? "")
    setEditing(true)
  }

  const confirmEdit = () => {
    onEdit(draft.trim() || null)
    setEditing(false)
  }

  const cancelEdit = () => {
    setEditing(false)
  }

  return (
    <div className="flex items-center gap-4 px-6 py-3">
      <div className="w-52 shrink-0">
        <p className="text-sm text-grey5">{field.primitiveTitle}</p>
      </div>
      <div className="flex-1">
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmEdit()
              if (e.key === "Escape") cancelEdit()
            }}
            className="w-full rounded border border-yellow1 bg-grey3 px-2 py-1 text-sm text-white focus:outline-hidden"
          />
        ) : (
          <span
            className={clsx(
              "text-sm",
              displayValue ? "text-white" : "italic text-grey5",
            )}
          >
            {displayValue ?? "—"}
          </span>
        )}
      </div>
      <div className="shrink-0">
        {editing ? (
          <div className="flex gap-1">
            <button
              onClick={confirmEdit}
              className="rounded p-1 text-success hover:bg-grey3"
              title={translate("QMS/SUPPLIER/APPLY")}
            >
              <IconCheck size={14} />
            </button>
            <button
              onClick={cancelEdit}
              className="rounded p-1 text-grey5 hover:bg-grey3"
              title={translate("QMS/COMMON/CANCEL")}
            >
              <IconX size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={startEdit}
            className="rounded p-1 text-grey5 hover:bg-grey3 hover:text-white"
            title={translate("QMS/COMMON/EDIT")}
          >
            <IconPencil size={14} />
          </button>
        )}
      </div>
    </div>
  )
}
